import { useState, type FormEvent } from 'react'
import type { Project, ReleaseMap } from '../api/types'
import { useMounted } from '../components/layout'
import { needsServer } from '../components/needsServer'
import { toast } from '../components/toasts'
import { useUnsavedChanges } from '../components/unsaved'
import { useBoardState, useStore } from '../store/context'
import { settingsWords } from './words'

const words = settingsWords.releases
const lines = (text: string) => text.split('\n').map(line => line.trim()).filter(Boolean)

export function ReleaseSettings({ project }: { project: Project }) {
  const store = useStore()
  const { offline } = useBoardState()
  const mounted = useMounted()
  const [edit, setEdit] = useState<ReleaseMap | null | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const map = edit === undefined ? project.release_map : edit
  const channels = map?.channels ?? []
  const dirty = edit !== undefined
  useUnsavedChanges(dirty)
  const change = (patch: Partial<ReleaseMap>) => { setEdit({ channels, no_release: map?.no_release ?? [], ...patch }); setProblem(null) }
  const channel = (index: number, patch: Partial<ReleaseMap['channels'][number]>) => change({ channels: channels.map((c, at) => at === index ? { ...c, ...patch } : c) })

  async function save(event: FormEvent) {
    event.preventDefault()
    if (store.getState().offline || busy || !dirty) return
    const normal = map ? { channels: channels.map(c => ({ name: c.name.trim(), tag: c.tag?.trim() || null, paths: lines(c.paths.join('\n')) })), no_release: lines(map.no_release.join('\n')) } : null
    if (normal?.channels.some(c => !c.name)) { setProblem(words.blank); return }
    if (normal && new Set(normal.channels.map(c => c.name)).size !== normal.channels.length) { setProblem(words.duplicate); return }
    setBusy(true); setProblem(null)
    const outcome = await store.setReleaseMap(project.id, normal)
    if (!mounted.current) return
    setBusy(false)
    if (outcome.ok) { setEdit(undefined); toast(settingsWords.saved) }
    else setProblem(outcome.message)
  }
  return <form className="setup-form" aria-label={words.title} onSubmit={event => void save(event)}>
    <p className="muted">{words.hint}</p>
    {!map && <p className="muted">{words.none}</p>}
    {channels.map((c, index) => <fieldset key={index} className="setup-form settings-release" aria-label={c.name || words.newRelease} disabled={busy}>
      <label className="field"><span>{words.name}</span><input maxLength={40} value={c.name} onChange={event => channel(index, { name: event.target.value })} /></label>
      <label className="field"><span>{words.tag}</span><input maxLength={40} value={c.tag ?? ''} onChange={event => channel(index, { tag: event.target.value || null })} /></label>
      <label className="field"><span>{words.paths}</span><textarea rows={3} value={c.paths.join('\n')} onChange={event => channel(index, { paths: event.target.value.split('\n') })} /></label>
      <div><button type="button" className="button button-quiet" onClick={() => change({ channels: channels.filter((_, at) => at !== index) })}>{words.remove}</button></div>
    </fieldset>)}
    <div><button type="button" className="button" disabled={busy || channels.length >= 20} onClick={() => change({ channels: [...channels, { name: '', tag: null, paths: [] }] })}>{words.add}</button></div>
    <label className="field"><span>{words.noRelease}</span><textarea rows={3} disabled={busy} value={map?.no_release.join('\n') ?? ''} onChange={event => change({ no_release: event.target.value.split('\n') })} /></label>
    <p className="muted">{settingsWords.flow.patterns}</p>
    {problem && <p role="alert" className="text-danger">{problem}</p>}
    <div className="form-actions">
      <button className="button button-primary" disabled={offline || busy || !dirty} {...needsServer(offline)}>{busy ? words.saving : words.save}</button>
      {map && <button type="button" className="button button-quiet" disabled={busy} onClick={() => { setEdit(null); setProblem(null) }}>{words.clear}</button>}
    </div>
  </form>
}
